import React, { useState, useRef, useEffect } from 'react';
import { Popup, Marker, GeoJSONSource } from 'mapbox-gl';
import { Coordinates } from '@wemap/geo';
import { Form, Spinner } from 'react-bootstrap';
import {
    CitywayRemoteRouter, DeutscheBahnRemoteRouter,
    IdfmRemoteRouter, Itinerary, OsrmRemoteRouter, OtpRemoteRouter,
    RemoteRouterManager, WemapMultiRemoteRouter,
    type RemoteRouterName
} from '@wemap/routers';

// NOTE: RouterResponse, RoutingMode and WemapMultiRemoteRouterOptions are not exported
// from the routers SDK public entry point (they were not exported by @wemap/routers-legacy
// either — this import was already unresolved before the migration). Declared locally so the
// module resolves; the routers/ tool has other pre-existing issues to fix separately.
type RoutingMode = 'CAR' | 'WALK' | 'BIKE' | 'BUS' | 'MULTI';
type RouterResponse = any;
type WemapMultiRemoteRouterOptions = any;
import mapboxgl from 'mapbox-gl';

import SimpleMap from './SimpleMap';

const defaultUrlType = {
    'osrm': 'https://routing-osrm.getwemap.com',
    'otp': 'https://data.mobilites-m.fr/api/routers/default/plan?routerId=prod&showIntermediateStops=true&minTransferTime=60&transferPenalty=60&numItineraries=3&walkBoardCost=300&arriveBy=false&ui_date=&walkSpeed=1.1112&walkReluctance=5&locale=fr_FR',
    'cityway': 'https://stage1.api.grandest2.cityway.fr/journeyplanner/api/opt/PlanTrips/json?DepartureType=COORDINATES&ArrivalType=COORDINATES&UserId=WEBRGE',
    'deutsche-bahn': 'https://deutsche-bahn-routing.getwemap.com',
    'idfm': 'https://idfm.getwemap.com/marketplace/navitia/coverage/fr-idf/journeys?count=3'
} as const;

const CUSTOM_URL = 'https://routing.getwemap.com';

// eslint-disable-next-line max-statements
const RouterComponent = () => {
    const [router, setRouter] = useState<RemoteRouterName>('idfm');
    const [baseUrl, setBaseUrl] = useState<string>('https://idfm.getwemap.com/marketplace/navitia/coverage/fr-idf/journeys');
    const [mode, setMode] = useState<RoutingMode>('MULTI');
    const [waypoints, setWaypoints] = useState<[Coordinates, Coordinates] | null>(null);
    const [custom, setCustom] = useState(false);
    const [pmr, setPMR] = useState(false);
    const [map, setMap] = useState<mapboxgl.Map | null>(null);
    const [itineraryObj, setItineraryObj] = useState<Itinerary | null>(null);
    const [loading, setLoading] = useState(false);
    const markers = useRef<{ start?: Marker, end?: Marker }>({});
    const selectedPos = useRef<Coordinates | null>(null);
    const start = useRef<Coordinates | null>(null);
    const end = useRef<Coordinates | null>(null);
    const mapboxPopup = useRef<Popup | null>(null);

    let remoteRouters = [{
        name: router,
        endpointUrl: baseUrl
    }];
    if (router === IdfmRemoteRouter.rname) {
        remoteRouters.push({
            name: OsrmRemoteRouter.rname,
            endpointUrl: defaultUrlType.osrm
        });
    }

    let opts: WemapMultiRemoteRouterOptions = {
        useStairs: !pmr
    };
    if (custom) {
        opts = {
            remoteRouters,
        }
        remoteRouters = [{
            name: WemapMultiRemoteRouter.rname,
            endpointUrl: CUSTOM_URL
        }];
    }

    const [routerResponse, setRouterResponse] = useState<RouterResponse | null>(null);

    useEffect(() => {
        if (baseUrl && mode && waypoints) {
            setLoading(true);
            RemoteRouterManager.getItinerariesWithFallback(remoteRouters, mode, waypoints, opts)
                .then(setRouterResponse).finally(() => setLoading(false));
        }
    }, [router, baseUrl, custom, mode, waypoints, pmr]);

    const buttons = `
        <div>
            <div id="itinerary-start" style="padding: 2px 4px; cursor: pointer;">Select as <strong>start</strong></div>
            <div id="itinerary-end" style="padding: 2px 4px; cursor: pointer;">Select as <strong>end</strong></div>
            <div id="itinerary-clear" style="padding: 2px 4px; cursor: pointer;">Clear start/end</div>
        </div>
    `;

    const tryToComputeItinerary = () => {
        if (start.current && end.current) {
            setWaypoints([start.current, end.current]);
        }
    };

    const closeMapboxPopup = () => {
        if (mapboxPopup.current) {
            mapboxPopup.current.remove();
            mapboxPopup.current = null;
        }
    };

    const createOrMoveMarker = (id: 'start' | 'end', pos: any, options: any) => {
        const marker = markers.current[id];
        if (!marker) {
            markers.current[id] = new Marker(options).setLngLat(pos).addTo(map!);
        } else {
            marker.setLngLat(pos);
        }
    };

    const updateStart = () => {
        start.current = selectedPos.current;
        createOrMoveMarker('start', [start.current!.longitude, start.current!.latitude], {
            color: '#00FF00'
        });
        closeMapboxPopup();
        tryToComputeItinerary();
    };

    const updateEnd = () => {
        end.current = selectedPos.current;
        createOrMoveMarker('end', [end.current!.longitude, end.current!.latitude], {
            color: '#FF0000'
        });
        closeMapboxPopup();
        tryToComputeItinerary();
    };

    const clearStartEnd = () => {
        start.current = null;
        end.current = null;
        if (markers.current.start) {
            markers.current.start.remove();
            delete markers.current.start;
        }
        if (markers.current.end) {
            markers.current.end.remove();
            delete markers.current.end;
        }

        closeMapboxPopup();
    };

    useEffect(() => {
        if (map) {
            map.on('click', (e: any) => {
                selectedPos.current = new Coordinates(e.lngLat.lat, e.lngLat.lng, null, 0);

                mapboxPopup.current = new Popup()
                    .setLngLat(e.lngLat)
                    .setHTML(buttons)
                    .addTo(map);

                document.getElementById('itinerary-start')!.addEventListener('click', () => updateStart());
                document.getElementById('itinerary-end')!.addEventListener('click', () => updateEnd());
                document.getElementById('itinerary-clear')!.addEventListener('click', () => clearStartEnd());
            });
        }
    }, [map]);

    useEffect(() => {
        if (routerResponse && routerResponse.itineraries.length) {
            setItineraryObj(routerResponse.itineraries[0]);
        }
    }, [routerResponse]);

    useEffect(() => {
        if (!map) return;
        if (itineraryObj) {
            let geometries: any = itineraryObj.legs.map((leg: any) => ({
                color: leg.transportInfo ? `#${leg.transportInfo.routeColor}` : '#000000',

                geometry: {
                    coordinates: leg.coords.map((coord: any) => [coord.lng, coord.lat]),
                    type: 'LineString'
                }
            }));
            geometries = geometries.map((geometry: any) => {
                return {
                    type: 'Feature',
                    geometry: geometry.geometry,
                    properties: {
                        'line-color': geometry.color,
                        'line-opacity': 0.95,
                        'line-width': 6
                    }
                };
            });

            if (geometries.length > 0) {
                const mySource = map.getSource('itinerary');

                if (mySource) {
                    (map.getSource('itinerary') as GeoJSONSource).setData({
                        type: 'FeatureCollection',
                        features: geometries
                    });
                } else {
                    map.addSource('itinerary', {
                        type: 'geojson',
                        data: {
                            type: 'FeatureCollection',
                            features: geometries
                        }
                    });

                    map.addLayer({
                        id: 'itinerary-line',
                        source: 'itinerary',
                        type: 'line',
                        layout: {
                            'line-join': 'round',
                            'line-cap': 'round'
                        },
                        paint: {
                            'line-color': {
                                type: 'identity',
                                property: 'line-color'
                            },
                            'line-opacity': 0.95,
                            'line-width': 6
                        }
                    });
                }
            }
        }
    }, [itineraryObj]);

    return (
        <div className="main-container">
            <div className="panel">
                <h3>Router</h3>
                <Form className="form-container" onSubmit={(e) => {
                    e.preventDefault();
                    tryToComputeItinerary();
                }}>
                    <Form.Group>
                        <Form.Select name="router"
                            id="router"
                            value={router}
                            onChange={(e) => {
                                setRouter(e.target!.value as any);
                                setBaseUrl(defaultUrlType[(e.target as any).value as keyof typeof defaultUrlType]);
                            }}>
                            <option value={OsrmRemoteRouter.rname}>OSRM</option>
                            <option value={OtpRemoteRouter.rname}>OTP</option>
                            <option value={CitywayRemoteRouter.rname}>Cityway</option>
                            <option value={DeutscheBahnRemoteRouter.rname}>Deutsche Bahn</option>
                            <option value={IdfmRemoteRouter.rname}>IDFM</option>
                        </Form.Select>
                    </Form.Group>
                    <Form.Group>
                        <Form.Check
                            type="checkbox"
                            id="custom"
                            name="custom"
                            value="custom"
                            label="Custom"
                            onChange={() => setCustom((customState) => !customState)}
                            />
                    </Form.Group>
                    <Form.Group>
                        <Form.Check
                            type="checkbox"
                            id="pmr"
                            name="pmr"
                            value="pmr"
                            label="PMR"
                            onChange={() => setPMR((pmrState) => !pmrState)}
                            />
                    </Form.Group>
                    <Form.Group>
                        <Form.Label htmlFor="url">Url</Form.Label>
                        <Form.Control
                            type="text"
                            name="url"
                            id="url"
                            value={baseUrl}
                            onChange={(e) => setBaseUrl(e.target.value)}
                        />
                    </Form.Group>
                    <Form.Group>
                        <Form.Select name="mode"
                            id="mode"
                            value={mode}
                            onChange={(e) => setMode(e.target!.value as RoutingMode)}>
                            <option value={'CAR'}>Car</option>
                            <option value={'WALK'}>Walk</option>
                            <option value={'BIKE'}>Bike</option>
                            <option value={'BUS'}>TC</option>
                            <option value={'MULTI'}>Multimode</option>
                        </Form.Select>
                    </Form.Group>
                </Form>
            </div>
            {routerResponse && routerResponse.itineraries.length ? (
                <div className="results-panel">
                    {routerResponse.itineraries.map((itinerary: any, index: any) => (
                        <div className={`itinerary ${itinerary === itineraryObj ? 'selected' : ''}`}
                            onClick={() => setItineraryObj(itinerary)}
                            key={index}>
                            <span>Itinerary {index + 1}</span>
                        </div>
                    ))}
                </div>
            ) : null}
            {loading ? (
                <div className="overlay">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            ) : null}
            <div style={{
                flexGrow: 1,
                flexShrink: 1,
                flexBasis: 'auto',
                minHeight: '300px'
            }}>
                <SimpleMap
                    style={{ width: '100%', height: '100%' }}
                    ref={(ref) => ref && ref.map ? setMap(ref.map) : null}
                />
            </div>
        </div>
    );
};

export default React.memo(RouterComponent);

